import { gql, type TypedDocumentNode } from "@apollo/client";

export type UpsertAchievementCodeMutationResponse = {
  upsertAchievementCode: {
    achievementCode: {
      asofDate: string;
      awardRemarks: string | null;
      awardYear: string | null;
      code: string;
      pgaId: string;
      referenceId: string | null;
    } | null;
    message: string;
    success: boolean;
  };
};

export type UpsertAchievementCodeMutationVariables = {
  input: {
    asofDate: string;
    awardRemarks?: string | null;
    awardYear?: string | null;
    code: string;
    codeType: string;
    pgaId: string;
    referenceId?: string | null;
  };
};

export const UPSERT_ACHIEVEMENT_CODE_MUTATION: TypedDocumentNode<
  UpsertAchievementCodeMutationResponse,
  UpsertAchievementCodeMutationVariables
> = gql`
  mutation UpsertAchievementCode($input: BackofficeUpsertAchievementCodeInput!) {
    upsertAchievementCode(input: $input) {
      success
      message
      achievementCode {
        pgaId
        code
        asofDate
        awardYear
        awardRemarks
        referenceId
      }
    }
  }
`;

export type DeleteAchievementCodeMutationResponse = {
  deleteAchievementCode: {
    achievementCode: {
      asofDate: string;
      code: string;
      pgaId: string;
    } | null;
    message: string;
    success: boolean;
  };
};

export type DeleteAchievementCodeMutationVariables = {
  input: {
    asofDate: string;
    code: string;
    codeType: string;
    pgaId: string;
  };
};

export const DELETE_ACHIEVEMENT_CODE_MUTATION: TypedDocumentNode<
  DeleteAchievementCodeMutationResponse,
  DeleteAchievementCodeMutationVariables
> = gql`
  mutation DeleteAchievementCode($input: BackofficeDeleteAchievementCodeInput!) {
    deleteAchievementCode(input: $input) {
      success
      message
      achievementCode {
        pgaId
        code
        asofDate
      }
    }
  }
`;

export type UpsertAchievementAttributeMutationResponse = {
  upsertAchievementAttribute: {
    achievementAttribute: {
      attributeCode: string;
      attributeType: string;
      cvar: string | null;
      dvar: string | null;
      nvar: number | null;
      pgaId: string;
    } | null;
    message: string;
    success: boolean;
  };
};

export type UpsertAchievementAttributeMutationVariables = {
  input: {
    attributeCode: string;
    attributeType: string;
    cvar?: string | null;
    dvar?: string | null;
    nvar?: number | null;
    pgaId: string;
  };
};

export const UPSERT_ACHIEVEMENT_ATTRIBUTE_MUTATION: TypedDocumentNode<
  UpsertAchievementAttributeMutationResponse,
  UpsertAchievementAttributeMutationVariables
> = gql`
  mutation UpsertAchievementAttribute($input: BackofficeUpsertAchievementAttributeInput!) {
    upsertAchievementAttribute(input: $input) {
      success
      message
      achievementAttribute {
        pgaId
        attributeType
        attributeCode
        cvar
        nvar
        dvar
      }
    }
  }
`;

export type DeleteAchievementAttributeMutationResponse = {
  deleteAchievementAttribute: {
    achievementAttribute: {
      attributeCode: string;
      attributeType: string;
      pgaId: string;
    } | null;
    message: string;
    success: boolean;
  };
};

export type DeleteAchievementAttributeMutationVariables = {
  input: {
    attributeCode: string;
    attributeType: string;
    pgaId: string;
  };
};

export const DELETE_ACHIEVEMENT_ATTRIBUTE_MUTATION: TypedDocumentNode<
  DeleteAchievementAttributeMutationResponse,
  DeleteAchievementAttributeMutationVariables
> = gql`
  mutation DeleteAchievementAttribute($input: BackofficeDeleteAchievementAttributeInput!) {
    deleteAchievementAttribute(input: $input) {
      success
      message
      achievementAttribute {
        pgaId
        attributeType
        attributeCode
      }
    }
  }
`;

export type UpsertNativeAchievementMutationResponse = {
  upsertNativeAchievement: {
    achievement: {
      id: string;
      achievementTypeId: string;
      title: string;
      recognizedAt: string;
      recognitionYear: number | null;
      remarks: string | null;
    } | null;
    message: string;
    success: boolean;
  };
};

export type UpsertNativeAchievementMutationVariables = {
  input: {
    achievementTypeId: string;
    expiresOn?: string | null;
    id?: string | null;
    managingSectionId?: string | null;
    pgaId: string;
    recognitionYear?: number | null;
    recognizedAt: string;
    remarks?: string | null;
    selfReported?: boolean | null;
    title?: string | null;
  };
};

export const UPSERT_NATIVE_ACHIEVEMENT_MUTATION: TypedDocumentNode<
  UpsertNativeAchievementMutationResponse,
  UpsertNativeAchievementMutationVariables
> = gql`
  mutation UpsertNativeAchievement($input: BackofficeUpsertNativeAchievementInput!) {
    upsertNativeAchievement(input: $input) {
      success
      message
      achievement {
        id
        achievementTypeId
        title
        recognizedAt
        recognitionYear
        remarks
      }
    }
  }
`;

export type DeleteNativeAchievementMutationResponse = {
  deleteNativeAchievement: {
    achievement: { id: string } | null;
    message: string;
    success: boolean;
  };
};

export type DeleteNativeAchievementMutationVariables = {
  input: {
    id: string;
    pgaId: string;
  };
};

export const DELETE_NATIVE_ACHIEVEMENT_MUTATION: TypedDocumentNode<
  DeleteNativeAchievementMutationResponse,
  DeleteNativeAchievementMutationVariables
> = gql`
  mutation DeleteNativeAchievement($input: BackofficeDeleteNativeAchievementInput!) {
    deleteNativeAchievement(input: $input) {
      success
      message
      achievement {
        id
      }
    }
  }
`;
